import React, { useState, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Typography,
  Chip,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Home as HomeIcon,
  CalendarMonth as CalendarIcon,
  Chat as MessagesIcon,
  School as SchoolIcon,
  MedicalServices as HealthIcon,
  SportsBasketball as ActivitiesIcon,
  Contacts as ContactsIcon,
  AccountBalance as MoneyIcon,
  EmojiEvents as DisciplineIcon,
  MenuBook as NotebookIcon,
  PhotoAlbum as MemoriesIcon,
  SwapHoriz as CustodyIcon,
  Description as DocumentsIcon,
  Mail as MailIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Keyboard as KeyboardIcon,
} from '@mui/icons-material';
import { colors } from '../../theme';
import { useHotkeys } from '../../context/HotkeysContext';
import { useFamily } from '../../context';

const AIAssistantDialog = lazy(() => import('../dialogs/AIAssistantDialog'));

const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 72;

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}

const navItems: NavItem[] = [
  { path: '/', label: 'Home', icon: <HomeIcon />, color: colors.primary },
  { path: '/calendar', label: 'Calendar', icon: <CalendarIcon />, color: colors.calendar },
  { path: '/messages', label: 'Messages', icon: <MessagesIcon />, color: colors.messages },
  { path: '/mail', label: 'Mail', icon: <MailIcon />, color: '#3B82F6' },
  { path: '/notebook', label: 'Notebook', icon: <NotebookIcon />, color: colors.notes },
  { path: '/memories', label: 'Scrapbook', icon: <MemoriesIcon />, color: colors.memories },
  { path: '/school', label: 'School', icon: <SchoolIcon />, color: colors.school },
  { path: '/health', label: 'Health', icon: <HealthIcon />, color: colors.health },
  { path: '/activities', label: 'Activities', icon: <ActivitiesIcon />, color: colors.activities },
  { path: '/contacts', label: 'Contacts', icon: <ContactsIcon />, color: colors.contacts },
  { path: '/money', label: 'Money', icon: <MoneyIcon />, color: colors.money },
  { path: '/discipline', label: 'Discipline', icon: <DisciplineIcon />, color: colors.discipline },
  { path: '/custody', label: 'Custody', icon: <CustodyIcon />, color: '#8B5CF6' },
  { path: '/documents', label: 'Documents', icon: <DocumentsIcon />, color: '#6B7280' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggle,
  mobileOpen,
  onMobileClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { openHelpDialog } = useHotkeys();
  const { currentFamily } = useFamily();
  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  const drawerWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  // Check if family has 2 households (co-parenting/custody scenario)
  const familyData = currentFamily?.data as { householdType?: string; households?: Record<string, unknown> } | undefined;
  const hasTwoHouseholds =
    familyData?.householdType === 'split' ||
    Object.keys(familyData?.households || {}).length >= 2;

  // Filter nav items based on family configuration
  const filteredNavItems = navItems.filter((item) => {
    // Only show Custody for 2-household families
    if (item.path === '/custody' && !hasTwoHouseholds) {
      return false;
    }
    return true;
  });

  const handleNavClick = (path: string) => {
    navigate(path);
    onMobileClose();
  };

  const isSelected = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Logo Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          minHeight: 64,
        }}
      >
        {!collapsed ? (
          <Box
            onClick={() => setAiDialogOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
              transition: 'opacity 0.2s',
            }}
          >
            <img
              src="/logo192.png"
              alt="FamilyPal"
              style={{ width: 36, height: 36, borderRadius: 8 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                letterSpacing: '-0.5px',
              }}
            >
              FamilyPal
            </Typography>
            <AIIcon sx={{ fontSize: 18, color: theme.palette.primary.main, ml: -0.5 }} />
          </Box>
        ) : (
          <Tooltip title="AI Assistant" placement="right" arrow>
            <Box
              onClick={() => setAiDialogOpen(true)}
              sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            >
              <img
                src="/logo192.png"
                alt="FamilyPal"
                style={{ width: 36, height: 36, borderRadius: 8 }}
              />
            </Box>
          </Tooltip>
        )}
        <IconButton onClick={onToggle} size="small" sx={{ ml: collapsed ? 0 : 'auto' }}>
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <Divider />

      {/* Navigation Items */}
      <List sx={{ flex: 1, py: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {filteredNavItems.map((item) => {
          const selected = isSelected(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
              <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
                <ListItemButton
                  selected={selected}
                  onClick={() => handleNavClick(item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 2 : 2.5,
                    '&.Mui-selected': {
                      backgroundColor: `${item.color}15`,
                      '& .MuiListItemIcon-root': {
                        color: item.color,
                      },
                      '& .MuiListItemText-primary': {
                        color: item.color,
                        fontWeight: 600,
                      },
                    },
                    '&:hover': {
                      backgroundColor: `${item.color}10`,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: collapsed ? 0 : 2,
                      justifyContent: 'center',
                      color: selected ? item.color : theme.palette.text.secondary,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: selected ? 600 : 400,
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* Settings */}
      <List sx={{ py: 1 }}>
        <ListItem disablePadding>
          <Tooltip title={collapsed ? 'Settings' : ''} placement="right" arrow>
            <ListItemButton
              selected={isSelected('/settings')}
              onClick={() => handleNavClick('/settings')}
              sx={{
                minHeight: 48,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 2 : 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: collapsed ? 0 : 2,
                  justifyContent: 'center',
                }}
              >
                <SettingsIcon />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>

      {/* Keyboard Shortcuts Hint */}
      {!collapsed && (
        <Box
          onClick={openHelpDialog}
          sx={{
            px: 2,
            py: 1.5,
            mx: 1.5,
            mb: 1.5,
            borderRadius: 1,
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
            },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <KeyboardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            Press
          </Typography>
          <Chip
            label="?"
            size="small"
            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
          />
          <Typography variant="caption" color="text.secondary">
            for shortcuts
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH_EXPANDED,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {aiDialogOpen && (
        <Suspense fallback={null}>
          <AIAssistantDialog open={aiDialogOpen} onClose={() => setAiDialogOpen(false)} />
        </Suspense>
      )}
    </>
  );
};

export { DRAWER_WIDTH_EXPANDED, DRAWER_WIDTH_COLLAPSED };
